import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    PDCAlpha as alpha,
    Link,
} from "@mzc-pdc/ui";
import theme from "@styles/theme";
import FieldEditBox from "@features/field-by-type/field-edit-box";
import MetadataFieldEditMode from "@features/metadata-fields/metadata-field-edit-mode";
import { useTranslation } from "react-i18next";
import Typography from "@components_v2/typography/typography";
import MetadataFieldReadMode from "@features/metadata-fields/metadata-field-read-mode";
import {
    IconCircleOutlined,
    IconCompleteCircle,
    IconMoreHoriz,
    IconOpenInNew,
    IconReturnCircle,
    IconSchema,
    IconVisibility,
    IconWarningTriangle,
} from "@mzc-cloudplex/icons";
import { CircledIcon } from "@components_v2/icon/circled-icon";
import useDialog from "@hooks/useDialog";
import useExtendedCustomMetadata from "@hooks/useExtendedCustomMetadata";
import useCustomContent from "@hooks/useCustomContent";
import DialogDeleteV2 from "@features/dialog/dialog-delete-v2";
import compareVersions, { VERSION_CM_11209_ADD_CUSTOM_METADATA } from "@cores/version";
import { useSelector } from "react-redux";
import { isLocal } from "@cores/enviromentExtension";
import DialogSelectValidatedCustomMetadata from "@features/dialog/custom-metadata/dialog-select-validated-custom-metadata";
import useCheckPermission from "@hooks/useCheckPermission";
import PanelPropertyTitle from "@components/view/components/panel-property-title";
import { Menu } from "@components_v2/menu";
import { TABLE_LIST_TYPE } from "@constants";

const LX_PURCHASE_FIELD_ID = "jWyYPagVkBnZ3VOp2bw7qDGL";
const LX_UAS_FIELD_ID = "PbV4RZWel5ODxONoJ8jBG172";
const LX_APP_FIELD_ID = "5rWELqb3jMO8BzQAYlae1kBg";

const PanelMetadata = ({
    id,
    domain,
    projectId,
    resourceId,
    useCustomMetadata = false,
    data,
    extendedMetadata = {},
    isDisabled,
    updateExtendedMetadata = () => {},
    getHiddenFieldValue,
}) => {
    const { t } = useTranslation();
    const { dialog, openDialog, closeDialog } = useDialog();
    const {
        parseCustomMetadata,
        clearCustomMetadata,
        customMetadata,
        customMetadataInfo,
        getCustomMetadataHiddenValue,
        getAssetCustomMetadata,
        renderHiddenMetadataPermissionConfirmModal,
    } = useExtendedCustomMetadata({
        domain,
        projectId,
        resourceId,
        extendedMetadata,
    });
    const { checkIsNull } = useCustomContent();
    const CUSTOM_CONTENTS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "CustomContents" });

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const [editValue, setEditValue] = useState({});
    const [actionPending, setActionPending] = useState({});
    const [isEditModeById, setIsEditModeById] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [parsedCustomMetadata, setParsedCustomMetadata] = useState(null);

    const isLxHausys = window.location.hostname.includes("lxhausys.");

    const onHandleClickClose = useCallback((id) => {
        setIsEditModeById((prevEditMode) => ({ ...prevEditMode, [id]: false }));
    }, []);

    const onHandleClickSubmit = useCallback(
        async (el) => {
            setActionPending((prev) => ({ ...prev, [el.id]: true }));
            await el.onSubmitEditValue(editValue[el.id]);
            onHandleClickClose(el.id);
            setActionPending((prev) => ({ ...prev, [el.id]: false }));
        },
        [editValue, onHandleClickClose],
    );

    const onHandleChangeField = useCallback(
        (id, value, validationError) => {
            setEditValue((prevEditValue) => ({
                ...prevEditValue,
                [id]: value,
            }));
            if ((isLocal || isLxHausys) && id === LX_PURCHASE_FIELD_ID) {
                const isPurchaseSelected = value === "구매";
                const uasField = customMetadata.find((field) => field.id === LX_UAS_FIELD_ID);
                if (uasField) {
                    uasField.isRequired = isPurchaseSelected;
                }
            }
            setValidationErrors((prevErrors) => ({ ...prevErrors, [id]: validationError }));
        },
        [customMetadata, isLxHausys],
    );

    const getItemTemplate = (el) => {
        if (isLocal || isLxHausys) {
            if (el.id === LX_UAS_FIELD_ID && el.value) {
                return (
                    <Grid container alignItems="center" gap={0.75}>
                        <Link
                            underline={"hover"}
                            color={"primary"}
                            onClick={() =>
                                window.open(
                                    `http://guas.lxhausys.com/cm/service/html.ncd?view=/lgerp/pd/retrievePDList&topId=UA&subId=GUAS030112&Caller=DDN&DDN_PR_NO=${el.value}`,
                                    "_blank",
                                )
                            }
                        >
                            {el.value}
                            <IconOpenInNew size={16} htmlColor={"inherit"} />
                        </Link>
                    </Grid>
                );
            }
            if (el.id === LX_APP_FIELD_ID && el.value) {
                return (
                    <Grid container alignItems="center" gap={0.75}>
                        <Link
                            underline={"hover"}
                            color={"primary"}
                            onClick={() =>
                                window.open(
                                    `http://uapproval.lxhausys.com:7010/lgchem/approval.front.document.RetrieveDocumentFormCmd.lgc?seq=${el.value}`,
                                    "_blank",
                                )
                            }
                        >
                            {el.value}
                            <IconOpenInNew size={16} htmlColor={"inherit"} />
                        </Link>
                    </Grid>
                );
            }
            if (el.id === "Lqa574J3evOMxvnjd2g8ZoxX") {
                if (el.value === "APPROVED") {
                    return (
                        <Grid container alignItems="center" gap={0.75}>
                            <IconCompleteCircle size={16} color="success" />
                            <Typography variant={"body1"} color={"success.main"}>
                                결재 완료
                            </Typography>
                        </Grid>
                    );
                } else if (el.value === "SUBMITTED") {
                    return (
                        <Grid container alignItems="center" gap={0.75}>
                            <IconCircleOutlined size={12} color="primary" />
                            <Typography variant={"body1"} color={"primary.main"}>
                                기안 완료
                            </Typography>
                        </Grid>
                    );
                } else if (el.value === "REJECTED") {
                    return (
                        <Grid container alignItems="center" gap={0.75}>
                            <IconReturnCircle size={16} color="warning" />
                            <Typography variant={"body1"} color={"warning.main"}>
                                반려 됨
                            </Typography>
                        </Grid>
                    );
                } else if (el.value === "CANCELED") {
                    return (
                        <Grid container alignItems="center" gap={0.75}>
                            <IconCircleOutlined size={12} htmlColor={theme.palette.greySecondary[500]} />
                            <Typography variant={"body1"} color={theme.palette.greySecondary[500]}>
                                결재 취소
                            </Typography>
                        </Grid>
                    );
                } else if (el.value === "NONE") {
                    return (
                        <Grid container alignItems="center" gap={0.75}>
                            <IconCircleOutlined size={12} htmlColor={theme.palette.greySecondary[500]} />
                            <Typography variant={"body1"} color={theme.palette.greySecondary[500]}>
                                기안 전
                            </Typography>
                        </Grid>
                    );
                }
            }
        }
        return (
            <React.Fragment>
                <Stack direction={"row"} alignItems={"center"} gap={1}>
                    <MetadataFieldReadMode
                        type={el.type}
                        isHidden={el?.isHidden && !el.isCheckHiddenValue}
                        value={el.value}
                        options={el.options}
                    />
                    {el?.isHidden &&
                        (el?.pending ? (
                            <i className="sprite sprite sprite-loading"></i>
                        ) : (
                            <IconButton
                                circled
                                size={"small"}
                                disabled={el.isCheckHiddenValue}
                                onClick={() => {
                                    domain === TABLE_LIST_TYPE.ASSET
                                        ? getCustomMetadataHiddenValue(el?.id)
                                        : getHiddenFieldValue;
                                }}
                            >
                                {el.isCheckHiddenValue ? (
                                    <IconVisibility size={16} />
                                ) : (
                                    <CircledIcon size={"24px"} padding={"4px"} color={theme.palette.secondary[50]}>
                                        <IconVisibility size={16} />
                                    </CircledIcon>
                                )}
                            </IconButton>
                        ))}
                </Stack>
            </React.Fragment>
        );
    };

    const getMetadataListItem = useCallback(
        ({ el, index, isCustomMetadata }) => {
            return (
                <ListItem
                    key={index}
                    sx={{
                        pt: index > 0 ? 1 : 0,
                        px: 1.5,
                        py: 0.25,
                        borderRadius: 0.5,
                        transition: "background-color 0.3s",

                        "&:not(:has(.MuiSwitch-root, .MuiAutocomplete-root, .category-select, .form-input, .MuiTextField-root, .MuiSelect-select))":
                            {
                                "&:hover": {
                                    backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                                },
                            },
                    }}
                >
                    <ListItemText
                        disableTypography
                        primary={
                            <>
                                <PanelPropertyTitle
                                    variant={"body2"}
                                    color={theme.palette.greySecondary[600]}
                                    subtitle={
                                        isCustomMetadata ? el.subtitle : t(`common::label::${el.subtitle}`, el.subtitle)
                                    }
                                    required={el.isRequired}
                                    showEditButton={
                                        el.isEditable &&
                                        !isEditModeById?.[el.id] &&
                                        (!el.isHidden || (el.isHidden && el.isCheckHiddenValue))
                                    }
                                    editButtonDisabled={el.isEditButtonDisabled || isDisabled}
                                    onClickEditButton={() => {
                                        setIsEditModeById({
                                            ...isEditModeById,
                                            [el?.id]: true,
                                        });
                                    }}
                                    tooltip={el.description}
                                />
                                {/*<Typography*/}
                                {/*    required={el.isRequired}*/}
                                {/*    variant={"body2"}*/}
                                {/*    color={theme.palette.greySecondary[600]}*/}
                                {/*    gutterBottom*/}
                                {/*>*/}
                                {/*    {el.subtitle}*/}
                                {/*</Typography>*/}
                                {/*{el.isEditable &&*/}
                                {/*    !isEditModeById?.[el.id] &&*/}
                                {/*    (!el.isHidden || (el.isHidden && el.isCheckHiddenValue)) && (*/}
                                {/*        <Tooltip title={t(`common::label::${"Edit"}`)} sx={{ ml: 1 }}>*/}
                                {/*            <IconButton*/}
                                {/*                className={"btn-edit"}*/}
                                {/*                size={"xsmall"}*/}
                                {/*                circled*/}
                                {/*                variant={"dashed"}*/}
                                {/*                disabled={el.isEditButtonDisabled}*/}
                                {/*                onClick={() => {*/}
                                {/*                    setIsEditModeById({*/}
                                {/*                        ...isEditModeById,*/}
                                {/*                        [el?.id]: true,*/}
                                {/*                    });*/}
                                {/*                }}*/}
                                {/*            >*/}
                                {/*                <IconEditSmall size={12} />*/}
                                {/*            </IconButton>*/}
                                {/*        </Tooltip>*/}
                                {/*    )}*/}
                            </>
                        }
                        secondary={
                            isEditModeById && !isEditModeById?.[el.id] ? (
                                <Grid container gap={1} alignItems={"center"} pt={1}>
                                    {getItemTemplate(el)}
                                </Grid>
                            ) : (
                                <FieldEditBox
                                    submitDisabled={
                                        editValue[el.id] === el.value ||
                                        actionPending?.[el.id] ||
                                        validationErrors[el.id]
                                    }
                                    submitPending={actionPending?.[el.id]}
                                    onClickSubmit={() => onHandleClickSubmit(el)}
                                    onClickClose={() => onHandleClickClose(el.id)}
                                >
                                    <MetadataFieldEditMode
                                        id={el.id}
                                        type={el.type}
                                        value={editValue[el.id]}
                                        options={el.options}
                                        placeholder={el.placeholder}
                                        disabled={actionPending?.[el.id] || !el.isEditable}
                                        isEdit={true}
                                        onChangeField={onHandleChangeField}
                                        validationRule={el.validationRule}
                                    />
                                </FieldEditBox>
                            )
                        }
                    />
                </ListItem>
            );
        },
        [
            editValue,
            actionPending,
            isEditModeById,
            validationErrors,
            onHandleClickSubmit,
            onHandleClickClose,
            onHandleChangeField,
        ],
    );

    const onHandleChangeCustomMetadata = async (customMetadata) => {
        const extendedMetadata = {
            contentSchemaId: customMetadata.id,
            data: {
                fields: customMetadata?.fields
                    ?.filter((el) => el.isEditable && !checkIsNull(el.defaultValue))
                    .map(({ id, defaultValue }) => ({ id, value: defaultValue }))
                    .flat(),
            },
        };
        await updateExtendedMetadata(extendedMetadata);
    };

    const onHandleDeleteCustomMetadata = async () => {
        await updateExtendedMetadata({ extendedMetadata: null });
        clearCustomMetadata();
    };

    const initEditValue = useCallback(() => {
        setEditValue((prevEditValue) => {
            const updatedEditValue = { ...prevEditValue };
            if (data) {
                data.forEach((group) =>
                    group?.attributions?.forEach((el) => {
                        updatedEditValue[el.id] = el.value;
                    }),
                );
            }
            if (customMetadata) {
                customMetadata.forEach((el) => {
                    updatedEditValue[el.id] = el.value;
                });
            }
            return updatedEditValue;
        });
    }, [data, customMetadata]);

    const initEditMode = useCallback(() => {
        setIsEditModeById((prevEditMode) => {
            const editMode = { ...prevEditMode };
            if (data) {
                data.forEach((group) => {
                    group.attributions?.forEach((attr) => {
                        editMode[attr.id] = false;
                    });
                });
            }
            if (customMetadata) {
                customMetadata.forEach((el) => {
                    editMode[el.id] = false;
                });
            }
            return editMode;
        });
    }, [data, customMetadata]);

    const onChangeCustomMetadataField = async (fieldId, value) => {
        try {
            if (!extendedMetadata) throw Error("extendedMetadata is not defined");
            const _extendedMetadata = {
                contentSchemaId: extendedMetadata?.contentSchemaId,
                contentId: extendedMetadata?.contentId,
                data: {
                    fields: [
                        {
                            id: fieldId,
                            value,
                        },
                    ],
                },
            };
            await updateExtendedMetadata(_extendedMetadata);
            await getAssetCustomMetadata({ assetId: id, projectId, contentId: extendedMetadata?.contentId });
        } catch (e) {
            console.log(e);
        }
    };

    const shouldShowUasLinkButton = useCallback(() => {
        if (!customMetadata) {
            return false;
        }
        if (!isLxHausys) {
            return false;
        }
        const purchaseField = customMetadata.find((field) => field.id === LX_PURCHASE_FIELD_ID);
        const uasField = customMetadata.find((field) => field.id === LX_UAS_FIELD_ID);
        if (purchaseField && uasField) {
            const isPurchaseSelected = purchaseField.value === "구매";
            if (isPurchaseSelected) {
                return true;
            }
        }
        return false;
    }, [customMetadata]);

    useEffect(() => {
        if (!customMetadata) return;
        if (!isEditModeById) initEditMode();
        initEditValue();

        if (customMetadata) {
            if (isLocal || isLxHausys) {
                const purchaseField = customMetadata.find((field) => field.id === LX_PURCHASE_FIELD_ID);
                if (purchaseField) {
                    const isPurchaseSelected = purchaseField.value === "구매";
                    const uasField = customMetadata.find((field) => field.id === LX_UAS_FIELD_ID);
                    if (uasField) {
                        uasField.isRequired = isPurchaseSelected;
                    }
                }
            }

            setParsedCustomMetadata(parseCustomMetadata(customMetadata, onChangeCustomMetadataField));
        } else {
            setParsedCustomMetadata(null);
        }
    }, [data, customMetadata]);

    if (!editValue) return <></>;
    return (
        <Box pt={2.5} pl={2.5} pr={1} pb={2.5}>
            {renderHiddenMetadataPermissionConfirmModal()}
            {data?.map((group, index) => (
                <div key={index}>
                    <Stack>
                        <Grid container>
                            <List
                                disablePadding
                                sx={{ p: 0, width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}
                            >
                                {group?.attributions?.map((el, index) =>
                                    getMetadataListItem({
                                        el,
                                        index,
                                        isCustomMetadata: false,
                                    }),
                                )}
                            </List>
                        </Grid>
                    </Stack>
                    {index < data.length - 1 && (
                        <Divider flexItem orientation={"horizontal"} sx={{ mt: 2.5, mb: 2.5 }} />
                    )}
                </div>
            ))}

            {useCustomMetadata && compareVersions(stage.version, VERSION_CM_11209_ADD_CUSTOM_METADATA) >= 0 && (
                <>
                    <Divider flexItem orientation={"horizontal"} sx={{ mt: 2.5, mb: 2.5 }} />
                    <Stack>
                        <Grid container flexDirection={"row"} sx={{ px: 1.5, pt: 1, pb: 3 }} alignItems={"center"}>
                            <Grid item flex={1}>
                                <Button
                                    color="greySecondary"
                                    startIcon={<IconSchema size={20} />}
                                    label="Button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openDialog("selectCustomMetadata");
                                    }}
                                    size="small"
                                    type="button"
                                    variant="outlined"
                                    disabled={!CUSTOM_CONTENTS_PERMISSIONS.EDIT || isDisabled}
                                >
                                    {t(`common::label::Custom Metadata`, `Custom Metadata`)}
                                </Button>
                            </Grid>
                            {shouldShowUasLinkButton() && (
                                <Grid item sx={{ mr: theme.spacing(2.5) }}>
                                    <Button
                                        size={"xsmall"}
                                        variant={"contained"}
                                        color={"greySecondary"}
                                        endIcon={<IconOpenInNew size={16} htmlColor={theme.palette.common.white} />}
                                        onClick={() =>
                                            window.open(
                                                "http://guas.lxhausys.com/cm/service/html.ncd?view=/lgerp/pd/retrievePDList&topId",
                                                "_blank",
                                            )
                                        }
                                    >
                                        UAS 품의서 조회
                                    </Button>
                                </Grid>
                            )}
                            <Grid item>
                                <Menu
                                    menuWidth={160}
                                    placement={"right"}
                                    type={"icon"}
                                    label={<IconMoreHoriz size={16} />}
                                    color={"inherit"}
                                    size={"medium"}
                                    list={[
                                        {
                                            label: t(`common::label::Unapply`, "Unapply"),
                                            onClick: (e) => {
                                                openDialog("deleteCustomMetadata");
                                            },
                                            disabled:
                                                Object.keys(extendedMetadata).length === 0 ||
                                                !CUSTOM_CONTENTS_PERMISSIONS.EDIT ||
                                                isDisabled,
                                        },
                                    ]}
                                />
                            </Grid>
                        </Grid>
                    </Stack>
                    {customMetadataInfo?.name && (
                        <Grid container sx={{ pl: 1.5, pr: 1.5, pt: 1, pb: 1.5 }} alignItems={"center"} gap={0.75}>
                            <IconSchema size={16} htmlColor={theme.palette.success.main} />
                            <Typography variant={"body1_500"} color={theme.palette.greySecondary.main}>
                                {customMetadataInfo?.name}
                            </Typography>
                        </Grid>
                    )}
                    {customMetadata &&
                        (parsedCustomMetadata && parsedCustomMetadata.length > 0 ? (
                            <Stack>
                                <Grid container>
                                    <List
                                        disablePadding
                                        sx={{ p: 0, width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}
                                    >
                                        {parsedCustomMetadata.map((el, index) =>
                                            getMetadataListItem({
                                                el,
                                                index,
                                                isCustomMetadata: true,
                                            }),
                                        )}
                                    </List>
                                </Grid>
                            </Stack>
                        ) : (
                            <Grid
                                container
                                flexDirection={"column"}
                                justifyContent={"center"}
                                alignItems={"center"}
                                gap={1}
                                sx={{
                                    border: `1px solid ${theme.palette.secondary[100]}`,
                                    borderRadius: "4px",
                                }}
                            >
                                <Grid item mt={5}>
                                    <Typography variant={"body1_500"} component={"span"}>
                                        {t(
                                            `common::msg::There is no custom metadata yet.`,
                                            `There is no custom metadata yet.`,
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item mb={5}>
                                    <Typography
                                        variant={"body1_400"}
                                        component={"span"}
                                        color={theme.palette.greySecondary[600]}
                                    >
                                        {t(
                                            `common::msg::To enter metadata, select one of the custom metadata options.`,
                                            `To enter metadata, select one of the custom metadata options.`,
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ))}
                </>
            )}
            {dialog === "selectCustomMetadata" && (
                <DialogSelectValidatedCustomMetadata
                    projectId={projectId}
                    contentSchemaId={extendedMetadata?.contentSchemaId}
                    onClickSubmit={onHandleChangeCustomMetadata}
                    onClickClose={closeDialog}
                />
            )}
            {dialog === "deleteCustomMetadata" && (
                <DialogDeleteV2
                    title={t(
                        `common::msg::Are you sure you want to turn off applying custom metadata?`,
                        "Are you sure you want to turn off applying custom metadata?",
                    )}
                    items={[extendedMetadata?.contentSchemaId]}
                    contents={(item, count) => (
                        <Stack direction={"row"}>
                            <Stack pr={1}>
                                <IconWarningTriangle size={16} color={"warning"} />
                            </Stack>
                            <Stack>
                                <Typography>
                                    {t(
                                        `common::msg::If you turn off Custom Apply All Data, your input data is gone and cannot be recovered.`,
                                        `If you turn off Custom Apply All Data, your input data is gone and cannot be recovered.`,
                                    )}
                                </Typography>
                            </Stack>
                        </Stack>
                    )}
                    deleteButtonLabel={t(`common::label::Unapply`, "Unapply")}
                    bulkDelete={false}
                    onClickSubmit={onHandleDeleteCustomMetadata}
                    afterMutation={() => setActionPending({})}
                    onClickClose={closeDialog}
                />
            )}
        </Box>
    );
};

export default PanelMetadata;
